import { Notification } from "@/utils/notificationUtils";
import { openBlobFile } from "@/utils/utils";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CommissionType } from "../../../../modules/commissions/batch/enums";
import { UpdateCommission } from "../../../../modules/commissions/batch/types";
import { commissionPostponedApi } from "../../../../modules/commissions/postponed/api";
import {
  DuplicatePostponedCommission,
  PostponedCommissionsExportRequest,
  PostponedCommissionsFilterPageRequest,
} from "../../../../modules/commissions/postponed/types";
import { EntityObject, OptimisticLockVersionList, TwoLevelEntityIdObject, TwoLevelEntityObject } from "../../../types";
import { COMMISSIONS_POSTPONED_QUERY_KEYS } from "./queryKeys";

export const useFilterPostponedCommissions = (payload: PostponedCommissionsFilterPageRequest, enabled?: boolean) => {
  return useQuery({
    queryKey: COMMISSIONS_POSTPONED_QUERY_KEYS.filterPostponedCommissions(payload),
    queryFn: async () => {
      const { data } = await commissionPostponedApi.filterPostponedCommissions(payload);

      return data;
    },
    placeholderData: keepPreviousData,
    enabled,
  });
};

export const useUpdatePostponedCommission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_POSTPONED_QUERY_KEYS.updatePostponedCommission(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<UpdateCommission>) => {
      const { data } = await commissionPostponedApi.updatePostponedCommission(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_POSTPONED_QUERY_KEYS.filterPostponedCommissions() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useResolvePostponedCommission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_POSTPONED_QUERY_KEYS.resolvePostponedCommission(),
    mutationFn: async (payload: OptimisticLockVersionList) => {
      const { data } = await commissionPostponedApi.resolvePostponedCommission(payload);

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_POSTPONED_QUERY_KEYS.filterPostponedCommissions() });
      Notification.itemUpdatedNotification(data.length);
    },
  });
};

export const useUnresolvePostponedCommission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_POSTPONED_QUERY_KEYS.unresolvePostponedCommission(),
    mutationFn: async (payload: OptimisticLockVersionList) => {
      const { data } = await commissionPostponedApi.unresolvePostponedCommission(payload);

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_POSTPONED_QUERY_KEYS.filterPostponedCommissions() });
      Notification.itemUpdatedNotification(data.length);
    },
  });
};

export const useCreateDuplicatedCommission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_POSTPONED_QUERY_KEYS.createDuplicatedCommission(),
    mutationFn: async (payload: EntityObject<DuplicatePostponedCommission>) => {
      const { data } = await commissionPostponedApi.createDuplicatedCommission(payload);

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_POSTPONED_QUERY_KEYS.filterPostponedCommissions() });

      if (data.type !== CommissionType.DUPLICATED_COMMISSION) {
        Notification.getNotification().warning({
          title: "common.warning",
          description: "commissions.batch.helpers.commissionIncludeFailure",
        });
      } else {
        Notification.itemUpdatedNotification();
      }
    },
  });
};

export const useUpdateDuplicatedCommission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_POSTPONED_QUERY_KEYS.updatePostponedCommission(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: TwoLevelEntityObject<UpdateCommission>) => {
      const { data } = await commissionPostponedApi.updateDuplicatedCommission(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_POSTPONED_QUERY_KEYS.filterPostponedCommissions() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteDuplicatedCommission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_POSTPONED_QUERY_KEYS.deleteDuplicatedCommission(),
    mutationFn: (payload: TwoLevelEntityIdObject) => {
      return commissionPostponedApi.deleteDuplicatedCommission(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMMISSIONS_POSTPONED_QUERY_KEYS.filterPostponedCommissions() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDownloadPostponedCommissionsExport = () => {
  return useMutation({
    mutationKey: COMMISSIONS_POSTPONED_QUERY_KEYS.downloadPostponedCommissionsExport(),
    mutationFn: async (payload: PostponedCommissionsExportRequest) => {
      const response = await commissionPostponedApi.downloadPostponedCommissionsExport(payload);
      openBlobFile(response, true);

      return response.data;
    },
  });
};
